"use client";

import { useState } from "react";
import { ArrowRight, Mail, Phone, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const serviceOptions = [{
  value: "Performance Marketing",
  label: "Performance Marketing"
}, {
  value: "Brand Transformation",
  label: "Brand Transformation"
}, {
  value: "Data Analytics",
  label: "Data Analytics"
}, {
  value: "Other",
  label: "Other"
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [messageLength, setMessageLength] = useState<number>(0);
  if (isSubmitted && !submitError) {
    return <div className="border-l-2 border-primary bg-card p-8">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
          [ MESSAGE DISPATCHED ]
        </p>
        <p className="font-default text-2xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="h-12 px-0 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@agency.com" className="h-12 px-0 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-000-0000" className="h-12 px-0 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Preferred Strategy Session
              </FormLabel>
              <FormControl>
                <Input type="date" className="h-12 px-0 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors [color-scheme:dark]" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem className="space-y-4">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Service of Interest
            </FormLabel>
            <FormControl>
              <div className="flex flex-wrap gap-2">
                {serviceOptions.map((option, i) => {
            const isSelected = field.value === option.value;
            return <Button key={option.value} data-index={i} type="button" onClick={() => field.onChange(option.value)} variant={isSelected ? "default" : "outline"} className={`font-mono text-xs uppercase tracking-widest px-4 py-3 ${isSelected ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "bg-transparent text-foreground border-border hover:border-primary"}`}>
                      {option.label}
                    </Button>;
          })}
              </div>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      maxLength: {
        value: 1000,
        message: "Max 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Project Details
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Textarea rows={4} placeholder="Tell us about your growth goals..." className="min-h-32 px-0 pt-2 pb-8 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors resize-none" {...field} onChange={e => {
            field.onChange(e);
            setMessageLength(e.target.value.length);
          }} />
                <div className="absolute bottom-2 right-0 font-mono text-xs text-muted-foreground">
                  {messageLength} / 1000
                </div>
              </div>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="newsletter" className="mt-0.5 border-border" />
        <label htmlFor="newsletter" className="font-mono text-xs uppercase tracking-widest text-muted-foreground cursor-pointer">
          Subscribe to Growth Insights — weekly performance data
        </label>
      </div>

      {submitError && <div className="border-l-2 border-destructive bg-destructive/10 p-4">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-sm uppercase tracking-widest">
        {isSubmitting ? <>Dispatching...</> : <>
            Let's Talk Strategy <ArrowRight className="size-4" />
          </>}
      </Button>
    </Form>;
}
export default function InitiateContact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4988" id="initiate-contact" className="bg-background text-foreground relative overflow-hidden py-24 md:py-40 px-6 md:px-12">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div className="absolute top-8 left-6 md:left-12 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        [ 03 / 03 ] — STRATEGY
      </div>
      <div className="absolute top-8 right-6 md:right-12 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hidden md:block">
        ROI // VELOCITY
      </div>

      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-8">
              <div className="flex items-center gap-3">
                <div className="h-px w-12 bg-primary" />
                <span className="font-mono text-xs uppercase tracking-widest text-primary">
                  Initiate Contact
                </span>
              </div>

              <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
                Scale your <span className="text-primary">growth</span>.
              </h2>

              <p className="font-default text-[17px] leading-relaxed text-muted-foreground">
                Ready to accelerate? Our team is prepared to audit your current digital footprint and build a high-velocity roadmap for your brand.
              </p>

              <div className="flex items-center gap-3 pt-2">
                <span className="relative flex size-2">
                  <span className="absolute inline-flex h-full w-full animate-ping bg-primary opacity-75" />
                  <span className="relative inline-flex size-2 bg-primary" />
                </span>
                <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                  Response Time: &lt; 24 HRS
                </span>
              </div>

              <div className="pt-8 border-t border-border space-y-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Direct Channels
                </p>

                <Link to="tel:+15550000000" className="group flex items-start gap-4 text-foreground">
                  <Phone className="size-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Phone
                    </div>
                    <div className="font-mono text-sm group-hover:text-primary transition-colors">
                      +1 555-000-0000
                    </div>
                  </div>
                </Link>

                <Link to="mailto:growth@catalystlab.com" className="group flex items-start gap-4 text-foreground">
                  <Mail className="size-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Email
                    </div>
                    <div className="font-mono text-sm group-hover:text-primary transition-colors break-all">
                      growth@catalystlab.com
                    </div>
                  </div>
                </Link>

                <Link to="#" className="group flex items-start gap-4 text-foreground">
                  <Send className="size-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Secure Channel
                    </div>
                    <div className="font-mono text-sm group-hover:text-primary transition-colors">
                      @catalyst_growth
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <FormProvider formKey="initiate_contact_form_contact" formId="initiate_contact_form_contact" sectionName="initiate-contact" defaultValues={{
            name: "",
            email: "",
            phone: "",
            preferred_date: "",
            service_interest: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            service_interest: {
              required: true
            },
            message: {
              maxLength: 1000
            }
          }} submitText="Let's Talk Strategy" successMessage="Message received. Our growth team will review your objectives and reach out within 24 hours." errorMessage="Submission failed. Please verify your details and try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}